import { ref } from 'vue'
import {
  showNewsletter,
  updateNewsletter,
  destroyNewsletter,
  sendTestNewsletter,
  sendNewsletter,
  cancelNewsletter,
} from '@/api/newsletters'
import type {
  Newsletter,
  UpdateNewsletterPayload,
  SendNewsletterPayload,
  SendNewsletterSuccess,
  InsufficientCreditsError,
} from '@/types/newsletter'

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  )
}

function isAxiosError(e: unknown): e is { response: { status: number; data: unknown } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  )
}

export function useNewsletter(organisationId: number) {
  const newsletter = ref<Newsletter | null>(null)
  const loading = ref<boolean>(false)
  const saving = ref<boolean>(false)
  const error = ref<string | null>(null)
  const formErrors = ref<Record<string, string[]>>({})
  const saved = ref<boolean>(false)

  async function load(newsletterId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      newsletter.value = await showNewsletter(organisationId, newsletterId)
    } catch {
      error.value = 'Failed to load newsletter.'
    } finally {
      loading.value = false
    }
  }

  async function save(newsletterId: number, payload: UpdateNewsletterPayload): Promise<boolean> {
    saving.value = true
    formErrors.value = {}
    saved.value = false
    try {
      newsletter.value = await updateNewsletter(organisationId, newsletterId, payload)
      saved.value = true
      return true
    } catch (e: unknown) {
      if (isValidationError(e)) {
        formErrors.value = e.response.data.errors
      }
      return false
    } finally {
      saving.value = false
    }
  }

  async function remove(newsletterId: number): Promise<boolean> {
    loading.value = true
    try {
      await destroyNewsletter(organisationId, newsletterId)
      return true
    } catch {
      return false
    } finally {
      loading.value = false
    }
  }

  async function sendTest(newsletterId: number): Promise<{ success: boolean; message: string }> {
    loading.value = true
    try {
      const result = await sendTestNewsletter(organisationId, newsletterId)
      // Refresh to update test_sends_remaining
      newsletter.value = await showNewsletter(organisationId, newsletterId)
      return { success: true, message: result.message }
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response.status === 422) {
        const data = e.response.data as { message?: string }
        return { success: false, message: data.message ?? 'Test send limit reached.' }
      }
      return { success: false, message: 'Failed to send test email.' }
    } finally {
      loading.value = false
    }
  }

  // Returns SendNewsletterSuccess on 202, throws with status=402 data for insufficient credits,
  // re-throws other errors.
  async function send(
    newsletterId: number,
    payload: SendNewsletterPayload,
  ): Promise<SendNewsletterSuccess | null> {
    loading.value = true
    formErrors.value = {}
    try {
      const result = await sendNewsletter(organisationId, newsletterId, payload)
      newsletter.value = await showNewsletter(organisationId, newsletterId)
      return result
    } finally {
      loading.value = false
    }
  }

  async function cancel(newsletterId: number): Promise<boolean> {
    loading.value = true
    try {
      newsletter.value = await cancelNewsletter(organisationId, newsletterId)
      return true
    } catch {
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    newsletter,
    loading,
    saving,
    error,
    formErrors,
    saved,
    load,
    save,
    remove,
    sendTest,
    send,
    cancel,
  }
}

// Re-export the type guard so calling components can narrow 402 errors
export function isInsufficientCreditsError(
  e: unknown,
): e is { response: { status: 402; data: InsufficientCreditsError } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    (e as { response: { status: number } }).response.status === 402
  )
}
